import { isEmpty, isNil, propEq, values, xprod } from 'ramda'
import { map, tap } from 'rxjs/operators'
import { Observable, startWith, SubscriptionLike, switchMap } from 'rxjs'
import connectSortBy, {
    SortByConnectorParams,
    SortByItem,
    SortByWidgetDescription,
} from 'instantsearch.js/es/connectors/sort-by/connectSortBy'

import { Component, forwardRef, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'

import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import { AlgoliaSortingDirection, AlgoliaSortingProperty, getIndexName, translateIndexName } from '@app-lib/algolia.lib'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { distinctUntilChangedEquals, pairwiseWithSeed } from '@app-lib/rxjs.lib'
import { Localization } from '@aa-app-localization'
import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'

@Component({
    selector: 'app-sort-by',
    template: `
        <app-select *ngIf="items" [control]="control" size="large">
            <option *ngFor="let item of items" [value]="item.label">
                {{ item.label }}
            </option>
        </app-select>
    `,
})
export class SortByComponent extends AbstractInstantsearchWidgetComponent<
SortByWidgetDescription,
SortByConnectorParams
> implements OnInit, OnDestroy {

    public override readonly widgetName = 'SortBy'

    /**
     * Translation keys for the different sorting properties.
     */
    private readonly sortingTranslationKeys: { [K in AlgoliaSortingProperty]: string } = <const>{
        [AlgoliaSortingProperty.Price]: 'catalog-index.sorting.price',
        [AlgoliaSortingProperty.ProductCode]: 'catalog-index.sorting.product-code',
        [AlgoliaSortingProperty.Range]: 'catalog-index.sorting.range',
        [AlgoliaSortingProperty.Name]: 'catalog-index.sorting.name',
        [AlgoliaSortingProperty.CreationDate]: 'catalog-index.sorting.creation-date',
    }

    /**
     * Unicode arrow characters to denote the selected sorting direction.
     */
    private readonly directionArrows: { [K in AlgoliaSortingDirection]: string } = {
        [AlgoliaSortingDirection.Ascending]: '↑',
        [AlgoliaSortingDirection.Descending]: '↓',
    }

    public control: FormControl
    public items: SortByItem[]
    private refineSubscription: SubscriptionLike

    constructor(
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        private readonly localization: LocalizationService,
        private analytics: AnalyticsService,
    ) {
        super()
    }

    /**
     * PROXIES – not DECORATES – the initialisation of the abstract superclass! Init and destroy of the super
     * class must be scheduled explicitly upon each locale change, as otherwise the sorting options cannot be
     * updated dynamically.
     */
    public override ngOnInit(): void {
        this.refineSubscription = this.localization.streamWithLocale([
            'catalog-index.sorting.price',
            'catalog-index.sorting.product-code',
            'catalog-index.sorting.range',
            'catalog-index.sorting.name',
            'catalog-index.sorting.creation-date',
        ]).pipe(
            distinctUntilChangedEquals(),
            map(({
                translations,
                locale,
            }) => this.getItemsList(translations, locale)),
            pairwiseWithSeed<SortByItem[]>([]),
            switchMap(([prevItems, currItems]) => this.refreshConnector(prevItems, currItems, isEmpty(prevItems))),
        ).subscribe(({
            items,
            sortingIndexName,
        }) => {
            this.items = items
            this.state?.refine(sortingIndexName)
        })

    }

    public override ngOnDestroy(): void {
        this.refineSubscription?.unsubscribe()
        super.ngOnDestroy()
    }

    /**
     * Destroys the current underlying connector to the algolia host, and creates a new one.
     * A new form-control is instantiated in the process. The form-control's
     */
    private refreshConnector(
        prevItems: SortByItem[],
        currItems: SortByItem[],
        isFirstRefresh: boolean,
    ): Observable<{ items: SortByItem[]; sortingIndexName: string }> {
        if (! isFirstRefresh) {
            super.ngOnDestroy()
        }

        const prevItem: SortByItem | undefined = isFirstRefresh ? undefined : this.findItemForLabel(
            this.control.value,
            prevItems,
        )

        const currItem: SortByItem = this.selectMatchingCurrItem(prevItem, currItems)
        this.control = new FormControl(currItem.label)

        this.createWidget(connectSortBy, { items: currItems })
        super.ngOnInit()

        return this.control.valueChanges.pipe(
            startWith(this.control.value),
            map((label) => ({
                items: currItems,
                sortingIndexName: this.findItemForLabel(label, currItems)!.value,
            })),
            tap((value) => {
                const filterName = this.getFilterNameForGtm(value.sortingIndexName)
                if (filterName.length) {
                    this.analytics.logSortOn(filterName)
                }
            }),
        )
    }

    private getFilterNameForGtm(indexName: string): string {
        const nameSplit = indexName.split('_')
        return nameSplit.length > 2 ? nameSplit.slice(2).join(' ') : ''
    }

    private selectMatchingCurrItem(prevItem: SortByItem | undefined, currItems: SortByItem[]): SortByItem {
        if (isNil(prevItem)) {
            return currItems[0]
        }

        return this.findItemForIndexName(
            translateIndexName(prevItem.value, this.localization.getCurrentLocale()),
            currItems,
        ) ?? currItems[0]
    }

    private getItemsList(translations: any, locale: Localization.Locale): SortByItem[] {
        const firstItem: SortByItem = this.createSortByItem(
            AlgoliaSortingProperty.CreationDate,
            AlgoliaSortingDirection.Descending,
            translations,
            locale,
        )

        const sortingItems: SortByItem[] = xprod(values(AlgoliaSortingProperty), values(AlgoliaSortingDirection))
            .map(([prop, direction]) => this.createSortByItem(prop, direction, translations, locale))
            .filter(({ value }) => value !== firstItem.value)

        return [firstItem, ...sortingItems]
    }

    private createSortByItem = (
        prop: AlgoliaSortingProperty,
        direction: AlgoliaSortingDirection,
        translations: any,
        locale: Localization.Locale): SortByItem => ({
        label: `${this.directionArrows[direction]} ${translations[this.sortingTranslationKeys[prop]]}`,
        value: getIndexName({
            locale,
            sorting: {
                property: prop,
                direction,
            },
        }),
    })

    private findItemForLabel(label: string, items: SortByItem[]): SortByItem | undefined {
        return items.find(propEq('label', label))
    }

    private findItemForIndexName(indexName: string, items: SortByItem[]): SortByItem | undefined {
        return items.find(propEq('value', indexName))
    }
}
